/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ==========================================================================
 * ORBITO CUSTOM LAYER
 * Upgrade-safe: lives only in custom.js. Never edit theme.js / vendor.js.
 * Provides: reduced-motion helper, safe custom-element registration, and a
 * theme-editor-safe section lifecycle registry that later sections build on.
 * ========================================================================== */
(function () {
  'use strict';

  var reduceMQ = (typeof window.matchMedia === 'function')
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: function () {}, removeEventListener: function () {}, addListener: function () {}, removeListener: function () {} };

  var Orbito = {
    /** True when the user has requested reduced motion. */
    reducedMotion: function () {
      return reduceMQ.matches;
    },

    /** Subscribe to reduced-motion preference changes. */
    onReducedMotionChange: function (cb) {
      if (typeof reduceMQ.addEventListener === 'function') {
        reduceMQ.addEventListener('change', cb);
      } else if (typeof reduceMQ.addListener === 'function') {
        reduceMQ.addListener(cb); // Safari < 14
      }
    },

    /** Unsubscribe a callback registered with onReducedMotionChange. */
    offReducedMotionChange: function (cb) {
      if (typeof reduceMQ.removeEventListener === 'function') {
        reduceMQ.removeEventListener('change', cb);
      } else if (typeof reduceMQ.removeListener === 'function') {
        reduceMQ.removeListener(cb);
      }
    },

    /** Register a custom element once (no double-define crashes on editor reload). */
    registerElement: function (name, ctor) {
      if (!('customElements' in window) || customElements.get(name)) return;
      customElements.define(name, ctor);
    },

    /* --- Section lifecycle registry (theme-editor safe) -------------------- */
    _entries: [],

    /**
     * Run `init(el)` for every element matching `selector` that is present now
     * or added later via a section load. `init` may return a state object that
     * is passed back to `teardown(el, state)` on section unload.
     */
    onSectionLoad: function (selector, init, teardown) {
      var entry = { selector: selector, init: init, teardown: teardown, instances: new WeakMap() };
      this._entries.push(entry);
      this._runInit(document, entry);
    },

    _runInit: function (root, entry) {
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll(entry.selector).forEach(function (node) {
        if (entry.instances.has(node)) return;
        var state = entry.init(node) || {};
        entry.instances.set(node, state);
      });
    },

    _runTeardown: function (container) {
      if (!container || !container.querySelectorAll) return;
      this._entries.forEach(function (entry) {
        container.querySelectorAll(entry.selector).forEach(function (node) {
          var state = entry.instances.get(node);
          if (state && entry.teardown) entry.teardown(node, state);
          entry.instances.delete(node);
        });
      });
    }
  };

  // Theme editor + dynamically loaded sections.
  document.addEventListener('shopify:section:load', function (e) {
    Orbito._entries.forEach(function (entry) { Orbito._runInit(e.target, entry); });
  });
  document.addEventListener('shopify:section:unload', function (e) {
    Orbito._runTeardown(e.target);
  });

  /* --- Desktop mega-menu keyboard accessibility -------------------------- *
   * Focal reveals panels on hover + CSS :focus-within but never syncs
   * aria-expanded for keyboard users and has no Escape / arrow handling.
   * We enhance the existing <desktop-navigation> element (never theme.js). */
  Orbito.onSectionLoad('desktop-navigation', function (nav) {
    var suppressFocusIn = false;
    function topItems() {
      return Array.prototype.slice.call(
        nav.querySelectorAll('.header__linklist-item.has-dropdown')
      );
    }
    function triggerOf(item) { return item.querySelector('.header__linklist-link'); }
    function panelOf(item) { return item.querySelector('.mega-menu, .nav-dropdown'); }

    function openItem(item) {
      var t = triggerOf(item), p = panelOf(item);
      if (t) t.setAttribute('aria-expanded', 'true');
      if (p) p.removeAttribute('hidden');
    }
    function closeItem(item) {
      var t = triggerOf(item), p = panelOf(item);
      if (t) t.setAttribute('aria-expanded', 'false');
      if (p) p.setAttribute('hidden', '');
    }

    function handleFocusIn(e) {
      if (suppressFocusIn) return;
      topItems().forEach(function (item) {
        if (item.contains(e.target)) openItem(item);
      });
    }
    function handleFocusOut(e) {
      topItems().forEach(function (item) {
        if (item.contains(e.target) && !item.contains(e.relatedTarget)) closeItem(item);
      });
    }
    function handleKeydown(e) {
      var triggers = topItems().map(triggerOf);
      var focusedTrigger = e.target.closest ? e.target.closest('.header__linklist-link') : null;

      if (e.key === 'Escape') {
        var openOne = topItems().filter(function (item) {
          var t = triggerOf(item);
          return t && t.getAttribute('aria-expanded') === 'true' && item.contains(document.activeElement);
        })[0];
        if (openOne) {
          var t = triggerOf(openOne);
          closeItem(openOne);
          suppressFocusIn = true;
          if (t) t.focus();
          suppressFocusIn = false;
          e.stopPropagation();
        }
        return;
      }
      if (focusedTrigger && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        var i = triggers.indexOf(focusedTrigger);
        if (i !== -1) {
          var n = e.key === 'ArrowRight'
            ? (i + 1) % triggers.length
            : (i - 1 + triggers.length) % triggers.length;
          triggers[n].focus();
          e.preventDefault();
        }
        return;
      }
      if (focusedTrigger && e.key === 'ArrowDown') {
        var item = focusedTrigger.closest('.header__linklist-item');
        if (item && item.classList.contains('has-dropdown')) {
          openItem(item);
          var first = panelOf(item) && panelOf(item).querySelector('a');
          if (first) { first.focus(); e.preventDefault(); }
        }
      }
    }

    nav.addEventListener('focusin', handleFocusIn);
    nav.addEventListener('focusout', handleFocusOut);
    nav.addEventListener('keydown', handleKeydown);
    return { fi: handleFocusIn, fo: handleFocusOut, kd: handleKeydown };
  }, function (nav, state) {
    nav.removeEventListener('focusin', state.fi);
    nav.removeEventListener('focusout', state.fo);
    nav.removeEventListener('keydown', state.kd);
  });

  /* --- <count-up> : count 0 -> data-to when scrolled into view -----------
   * Reduced motion or no IntersectionObserver -> show the final value at once.
   * Attrs: data-to (required number), data-decimals, data-prefix, data-suffix. */
  Orbito.registerElement('count-up', class extends HTMLElement {
    connectedCallback() {
      if (this._orbitoInit) return;
      this._orbitoInit = true;

      var to = parseFloat(this.getAttribute('data-to'));
      if (isNaN(to)) return;
      var decimals = parseInt(this.getAttribute('data-decimals') || '0', 10);
      var prefix = this.getAttribute('data-prefix') || '';
      var suffix = this.getAttribute('data-suffix') || '';
      var self = this;
      var render = function (v) { self.textContent = prefix + v.toFixed(decimals) + suffix; };

      if (Orbito.reducedMotion() || !('IntersectionObserver' in window)) { render(to); return; }
      render(0);

      var duration = 1400;
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self._io.disconnect();
          var start = null;
          var tick = function (ts) {
            if (start === null) start = ts;
            var p = Math.min((ts - start) / duration, 1);
            var eased = 1 - Math.pow(1 - p, 3);
            render(to * eased);
            if (p < 1) requestAnimationFrame(tick); else render(to);
          };
          requestAnimationFrame(tick);
        });
      }, { threshold: 0.4 });
      this._io.observe(this);
    }
    disconnectedCallback() { if (this._io) this._io.disconnect(); }
  });

  window.Orbito = Orbito;
})();

(function () {
  'use strict';
  var Orbito = window.Orbito;
  if (!Orbito) return;

  var HOVER_FINE_MQ = (typeof window.matchMedia === 'function')
    ? window.matchMedia('(hover: hover) and (pointer: fine)')
    : null;

  var BODY_CLASS = 'orbito-kv-cursor-on';
  var CURSOR_ICONS =
    '<svg class="orbito-kv-cursor__icon orbito-kv-cursor__icon--prev" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
    '<svg class="orbito-kv-cursor__icon orbito-kv-cursor__icon--next" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';

  /* One shared, body-level cursor node for every <slide-show> on the page,
     ref-counted so it is created lazily and only removed once no instance
     needs it any more. */
  var cursorNode = null;
  var cursorRefCount = 0;

  function getCursorNode() {
    if (!cursorNode) {
      cursorNode = document.createElement('div');
      cursorNode.className = 'orbito-kv-cursor';
      cursorNode.setAttribute('aria-hidden', 'true');
      cursorNode.innerHTML = CURSOR_ICONS;
      document.body.appendChild(cursorNode);
    }
    cursorRefCount++;
    return cursorNode;
  }

  function releaseCursorNode() {
    cursorRefCount = Math.max(0, cursorRefCount - 1);
    if (cursorRefCount === 0 && cursorNode && cursorNode.parentNode) {
      cursorNode.parentNode.removeChild(cursorNode);
      cursorNode = null;
    }
  }

  function gatePasses() {
    return !!(HOVER_FINE_MQ && HOVER_FINE_MQ.matches) &&
      !Orbito.reducedMotion() &&
      !(window.Shopify && window.Shopify.designMode);
  }

  /* Turn the indicator + click-to-navigate ON for one <slide-show> element. */
  function activate(el, state) {
    if (state.active) return;
    state.active = true;

    var node = getCursorNode();
    document.body.classList.add(BODY_CLASS);

    var pendingX = 0;
    var pendingY = 0;
    var hasPending = false;

    function applyFrame() {
      state.raf = null;
      if (!hasPending) return;
      var rect = el.getBoundingClientRect();
      var midpoint = rect.left + rect.width / 2;
      var side = pendingX < midpoint ? 'prev' : 'next';
      node.style.setProperty('--orbito-cursor-x', pendingX + 'px');
      node.style.setProperty('--orbito-cursor-y', pendingY + 'px');
      node.classList.toggle('is-prev', side === 'prev');
      node.classList.toggle('is-next', side === 'next');
      // FIX (correctness/blocker): reveal on the tracked move itself, not only
      // on mouseenter. custom.js is deferred and can attach this listener
      // after the pointer is already resting over the full-bleed hero, in
      // which case mouseenter never fires for it; mousemove keeps firing and
      // must be what reveals the disc. Idempotent - removal stays in leave.
      node.classList.add('is-visible');
    }

    function onMouseMove(e) {
      pendingX = e.clientX;
      pendingY = e.clientY;
      hasPending = true;
      if (state.raf === null) state.raf = requestAnimationFrame(applyFrame);
    }

    function onMouseLeave() {
      if (state.raf !== null) {
        cancelAnimationFrame(state.raf);
        state.raf = null;
      }
      hasPending = false;
      node.classList.remove('is-visible', 'is-prev', 'is-next');
    }

    function onClick(e) {
      if (e.target && e.target.closest && e.target.closest('a, button, page-dots')) return;
      var rect = el.getBoundingClientRect();
      var midpoint = rect.left + rect.width / 2;
      if (e.clientX < midpoint) {
        if (typeof el.previous === 'function') el.previous();
      } else if (typeof el.next === 'function') {
        el.next();
      }
    }

    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('click', onClick);

    state.node = node;
    state.handlers = { move: onMouseMove, leave: onMouseLeave, click: onClick };
  }

  /* Full teardown for one <slide-show>: listeners, shared node ref, body
     class (only dropped once no instance needs it). Safe to call twice. */
  function deactivate(el, state) {
    if (!state.active) return;
    state.active = false;

    if (state.raf !== null) {
      cancelAnimationFrame(state.raf);
      state.raf = null;
    }
    if (state.handlers) {
      el.removeEventListener('mousemove', state.handlers.move);
      el.removeEventListener('mouseleave', state.handlers.leave);
      el.removeEventListener('click', state.handlers.click);
      state.handlers = null;
    }
    if (state.node) {
      state.node.classList.remove('is-visible', 'is-prev', 'is-next');
      state.node = null;
    }
    releaseCursorNode();
    if (cursorRefCount === 0) {
      document.body.classList.remove(BODY_CLASS);
    }
  }

  /* Every live <slide-show> instance, tracked so a RUNTIME reduced-motion
     toggle can activate/deactivate all of them without a page reload. */
  var instances = [];

  function reconcileAll() {
    var passes = gatePasses();
    instances.forEach(function (entry) {
      if (passes && !entry.state.active) activate(entry.el, entry.state);
      else if (!passes && entry.state.active) deactivate(entry.el, entry.state);
    });
  }

  // FIX (motion-gate): react to prefers-reduced-motion changing mid-session.
  // When it turns ON, tear the disc + listeners + body class down immediately
  // (the CSS cursor:none rule also stops matching on its own, so the native
  // cursor and disc never disagree). When it turns back OFF, re-activate.
  Orbito.onReducedMotionChange(reconcileAll);

  Orbito.onSectionLoad('slide-show', function (el) {
    var entry = { el: el, state: { active: false, node: null, handlers: null, raf: null } };
    instances.push(entry);
    if (gatePasses()) activate(el, entry.state);
    return entry;
  }, function (el, entry) {
    deactivate(el, entry.state);
    var idx = instances.indexOf(entry);
    if (idx !== -1) instances.splice(idx, 1);
  });
})();


/* === EFFECT #6 · Shop-by-category tile UI polish (+ fix reveal trap) === */
(function () {
  'use strict';
  var Orbito = window.Orbito;
  if (!Orbito) return;

  var STAGGER_MS = 60;
  var MAX_STAGGER_STEPS = 8;

  // IMPORTANT (review fix): selector targets the <collection-list> CUSTOM
  // ELEMENT that Focal renders *inside* the section wrapper
  // (sections/collection-list.liquid L188 / L194:
  // <collection-list ... class="list-collections ...">), NOT the wrapper's
  // own `.shopify-section--collection-list` schema class. On
  // shopify:section:load / shopify:section:unload, Orbito._runInit /
  // _runTeardown (assets/custom.js) call root.querySelectorAll(selector)
  // with root === e.target === the section WRAPPER itself; querySelectorAll
  // never matches its own root node. A wrapper-class selector would only
  // ever match on the very first Orbito.onSectionLoad(document) call and
  // then silently match nothing on every later theme-editor edit/reorder of
  // this section -- the previous IntersectionObserver would leak (never
  // disconnected) and the re-rendered tiles would never get the
  // reveal-strip/entrance again. <collection-list> is a descendant of the
  // wrapper, so it is found by querySelectorAll on first load AND on every
  // subsequent section reload/teardown.
  Orbito.onSectionLoad('collection-list', function (el) {
    var items = Array.prototype.slice.call(el.querySelectorAll('.list-collections__item'));
    if (!items.length) return {};

    // Strip Focal's bare `reveal` / `reveal-visibility` attributes inside
    // this section. The CSS neutralizer (custom.css) already forces them
    // visible unconditionally, but removing the attributes here makes this
    // script the single source of truth for this section's entrance
    // motion instead of leaving a second, unobserved reveal mechanism
    // still attached to the markup.
    Array.prototype.forEach.call(el.querySelectorAll('[reveal], [reveal-visibility]'), function (node) {
      node.removeAttribute('reveal');
      node.removeAttribute('reveal-visibility');
    });

    // Only animate when the merchant actually opted in via this section's
    // "Reveal on scroll" setting (default OFF). Focal adds a
    // `reveal-on-scroll` attribute to <collection-list> only when
    // section.settings.reveal_on_scroll is true, so its presence is a
    // reliable signal without reading section settings from JS.
    var revealEnabled = el.hasAttribute('reveal-on-scroll');

    // Carousel tiles are excluded even when reveal-on-scroll is on: a
    // viewport-rooted IntersectionObserver can't see sideways, so tiles
    // scrolled off-screen to the right of the strip would otherwise sit at
    // opacity:0 until the shopper scrolls them into view. Reachable, not a
    // permanent trap, but not the intended "reveal" feel -- carousel tiles
    // ship immediately visible instead, with hover polish still applying.
    var isCarousel = el.hasAttribute('scrollable');

    var canAnimate = revealEnabled && !isCarousel && !Orbito.reducedMotion() && ('IntersectionObserver' in window);

    if (!canAnimate) return {};

    items.forEach(function (item, i) {
      item.classList.add('orbito-cl-item--pre');
      item.style.setProperty('--orbito-cl-delay', (Math.min(i, MAX_STAGGER_STEPS) * STAGGER_MS) + 'ms');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('orbito-cl-item--in');
        entry.target.classList.remove('orbito-cl-item--pre');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.2 });

    items.forEach(function (item) { io.observe(item); });

    return { io: io };
  }, function (el, state) {
    if (state && state.io) state.io.disconnect();
  });
})();


/* === ORBITO VIDEO BAND (orbito-video) — reduced-motion-aware autoplay === */
(function () {
  'use strict';
  var Orbito = window.Orbito;
  if (!Orbito) return;

  Orbito.onSectionLoad('[data-orbito-video]', function (section) {
    var video = section.querySelector('.orbito-video__media');
    var toggle = section.querySelector('[data-orbito-video-toggle]');
    if (!video || !section.hasAttribute('data-has-video')) return {};

    var userPaused = false;

    function setPressed(playing) {
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }
    function tryPlay() {
      if (userPaused || Orbito.reducedMotion()) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    var io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) tryPlay(); else video.pause();
          });
        }, { threshold: 0.25 })
      : null;
    if (io) io.observe(video); else tryPlay();

    function onPlay() { setPressed(true); }
    function onPause() { setPressed(false); }
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);

    function onToggle() {
      if (video.paused) { userPaused = false; tryPlay(); }
      else { userPaused = true; video.pause(); }
    }
    if (toggle) toggle.addEventListener('click', onToggle);

    function onMotionChange() { if (Orbito.reducedMotion()) video.pause(); else tryPlay(); }
    Orbito.onReducedMotionChange(onMotionChange);

    setPressed(false);
    return { io: io, video: video, toggle: toggle, onToggle: onToggle, onPlay: onPlay, onPause: onPause, onMotionChange: onMotionChange };
  }, function (section, state) {
    if (!state) return;
    if (state.io) state.io.disconnect();
    if (state.video) {
      state.video.removeEventListener('play', state.onPlay);
      state.video.removeEventListener('pause', state.onPause);
      state.video.pause();
    }
    if (state.toggle && state.onToggle) state.toggle.removeEventListener('click', state.onToggle);
    if (state.onMotionChange) Orbito.offReducedMotionChange(state.onMotionChange);
  });
})();


/* ==========================================================================
   BRANDED CURSOR — mark DARK images so the light cursor is used over them.
   Shopify serves images from the same origin (www.orbito.us/cdn/...), so the
   canvas is not tainted and we can read pixels. Anything that fails (tainted,
   undecodable) is simply skipped and keeps the default dark cursor.
   Pairs with the cursor rules in custom.css.
   ========================================================================== */
(function () {
  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var SIZE = 12;          // downscale each image to 12x12 to average it cheaply
  var THRESHOLD = 0.5;    // relative luminance below this = "dark"
  var canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  var ctx = canvas.getContext && canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;

  function luminance(img) {
    try {
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      var d = ctx.getImageData(0, 0, SIZE, SIZE).data, sum = 0, n = 0;
      for (var i = 0; i < d.length; i += 4) {
        sum += (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
        n++;
      }
      return n ? sum / n : null;
    } catch (e) {
      return null; // tainted canvas — bail quietly
    }
  }

  function classify(img) {
    if (img.hasAttribute('data-cursor-done') || !img.naturalWidth) return;
    img.setAttribute('data-cursor-done', '1');
    var l = luminance(img);
    if (l !== null && l < THRESHOLD) img.setAttribute('data-cursor', 'light');
  }

  function scan() {
    var imgs = document.querySelectorAll('img:not([data-cursor-done])');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete) classify(img);
      else img.addEventListener('load', function () { classify(this); }, { once: true });
    }
  }

  var t;
  function scanSoon() { clearTimeout(t); t = setTimeout(scan, 200); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  if (window.MutationObserver) {
    new MutationObserver(scanSoon).observe(document.documentElement, { childList: true, subtree: true });
  }
})();


/* ==========================================================================
   HEADER COLLAPSE v2 — REMOVED 2026-08-11 at the client's request.
   The scroll-collapse ("pill") transition is disabled; the header now stays a
   static transparent layer that colour-syncs dark<->light on the homepage,
   matching docs/sample/preview.html. Because this toggle no longer adds the
   `.is-compact` class, the collapse CSS in custom.css stays inert.
   FULL BACKUP + restore steps: snippets/orbito-header-collapse.backup.liquid
   ========================================================================== */


/* ==========================================================================
   HOMEPAGE HEADER FOLLOWS THE HERO SLIDE
   The see-through homepage header sits over the hero slideshow, whose slides
   mix dark and light images, so no single header colour reads on all of them.
   The header takes the ACTIVE slide's own "Text color" setting (theme editor >
   Slideshow > slide > Text color): over a slide whose text colour is light the
   header text is that light colour with the white logo; over a slide whose
   text colour is dark, the header text is that dark colour with the regular
   logo. custom.css applies it only while Focal's see-through state
   (.header--transparent) is on — hover and scroll already switch to the solid
   bar. Skipped where the Orbito World section runs its own header colouring.
   ========================================================================== */
(function () {
  'use strict';
  if (!window.Orbito || !window.MutationObserver) return;

  var LIGHT_INK = 0.5;   // relative luminance at or above this = light text = dark slide

  function luminance(triplet) {
    var c = (triplet || '').split(',').map(function (x) { return parseFloat(x) / 255; });
    if (c.length < 3 || c.some(isNaN)) return null;
    var f = function (v) { return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
  }

  function header() { return document.querySelector('store-header[transparent]'); }

  // Only the slideshow that is the first section on the page sits under the header.
  function isHero(show) {
    if (document.querySelector('.shopify-section--orbito-world')) return false;
    var first = document.querySelector('#main > .shopify-section');
    return !!first && first.contains(show);
  }

  function applySlide(item) {
    var h = header();
    if (!h || !item) return;
    var ink = getComputedStyle(item).getPropertyValue('--text-color').trim(), l = luminance(ink);
    if (l === null) return;
    var light = l >= LIGHT_INK;
    h.style.setProperty('--orbito-slide-ink', ink);
    // cart-count number must contrast with a bubble filled in the ink colour
    h.style.setProperty('--orbito-slide-bubble', light ? '17, 17, 17' : '255, 255, 255');
    h.classList.add('orbito-slide-adaptive');
    h.classList.toggle('orbito-slide-light-ink', light);
  }

  function clearHeader() {
    var h = header();
    if (!h) return;
    h.classList.remove('orbito-slide-adaptive', 'orbito-slide-light-ink');
    h.style.removeProperty('--orbito-slide-ink');
    h.style.removeProperty('--orbito-slide-bubble');
  }

  function activeItem(show) {
    var items = Array.prototype.slice.call(show.querySelectorAll('slide-show-item'));
    return items.filter(function (i) { return !i.hasAttribute('hidden'); })[0] || items[0] || null;
  }

  window.Orbito.onSectionLoad('slide-show', function (show) {
    if (!isHero(show)) return {};
    applySlide(activeItem(show));
    // A slide is selected when Focal removes its `hidden` attribute.
    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) { if (!r.target.hasAttribute('hidden')) applySlide(r.target); });
    });
    show.querySelectorAll('slide-show-item').forEach(function (i) {
      mo.observe(i, { attributes: true, attributeFilter: ['hidden'] });
    });
    return { mo: mo, hero: true };
  }, function (show, state) {
    if (state.mo) state.mo.disconnect();
    if (state.hero) clearHeader();
  });

  // Theme editor: a reloaded header section loses the classes — re-apply the current slide.
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target.querySelector || !e.target.querySelector('store-header')) return;
    var show = document.querySelector('#main > .shopify-section slide-show');
    if (show && isHero(show)) applySlide(activeItem(show));
  });
})();


/* ==========================================================================
   SERIES MEGA MENU — switch panels from the left rail
   Markup: snippets/orbito-series-menu.liquid. Hovering or focusing a series
   on the left shows that series' products on the right; clicking still goes
   to the collection. Panels are rendered up front, so the first one is
   already correct without JS and every series stays reachable by its link.
   ========================================================================== */
(function () {
  'use strict';
  if (!window.Orbito) return;

  window.Orbito.onSectionLoad('.orbito-series-menu', function (menu) {
    var railItems = Array.prototype.slice.call(menu.querySelectorAll('.orbito-series-menu__rail-item'));
    var panels = Array.prototype.slice.call(menu.querySelectorAll('.orbito-series-menu__panel'));
    if (railItems.length < 2) return {};

    function show(index) {
      railItems.forEach(function (item) {
        item.classList.toggle('is-active', item.getAttribute('data-series-index') === index);
      });
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-series-index') !== index;
      });
    }

    function onActivate(event) {
      show(event.currentTarget.getAttribute('data-series-index'));
    }

    railItems.forEach(function (item) {
      item.addEventListener('mouseenter', onActivate);
      item.addEventListener('focus', onActivate);
    });

    // Reopening the menu should start from the first series again.
    function onClose() { if (menu.hidden) show('0'); }
    var observer = new MutationObserver(onClose);
    observer.observe(menu, { attributes: true, attributeFilter: ['hidden'] });

    return { railItems: railItems, onActivate: onActivate, observer: observer };
  }, function (menu, state) {
    if (state.observer) state.observer.disconnect();
    (state.railItems || []).forEach(function (item) {
      item.removeEventListener('mouseenter', state.onActivate);
      item.removeEventListener('focus', state.onActivate);
    });
  });
})();


/* ==========================================================================
   PRODUCT GALLERY — show every photo until a colour is chosen
   orbito-gallery-show-all

   Shopify always preselects a variant, so Focal opens the product page with the
   gallery already filtered to one colour. Here the page opens showing every
   photo, and filtering begins the moment the shopper picks a colour. The variant
   stays selected underneath, so price, SKU, Add to cart and Buy it now behave
   exactly as before — this only changes what the gallery shows.

   Each photo carries its colour in data-orbito-option (set in
   snippets/product-media.liquid), so that first filter needs no product refetch
   and works even when the shopper clicks the colour that was already selected.
   Afterwards the theme owns filtering again: its own handler skips the rebuild
   while nothing is hidden, so this re-applies the filter once and steps aside.
   ========================================================================== */
(function () {
  'use strict';
  if (!window.Orbito) return;

  window.Orbito.onSectionLoad('product-media', function (mediaElement) {
    var carousel = mediaElement.querySelector('flickity-carousel');
    var items = Array.prototype.slice.call(mediaElement.querySelectorAll('[data-media-id]'));
    var tagged = items.filter(function (item) { return item.getAttribute('data-orbito-option'); });
    var wasFiltered = items.some(function (item) { return item.classList.contains('is-filtered'); });
    if (!carousel || !tagged.length || !wasFiltered) return {};

    // A ?variant= link means the shopper already arrived on a colour — from a swatch on a
    // listing, a shared link, or going back. Leave the theme's filtering in place there;
    // opening the whole gallery would show them other colours they did not ask for.
    if (/[?&]variant=/.test(window.location.search)) return {};

    items.forEach(function (item) { item.classList.remove('is-filtered'); });
    if (typeof carousel.reload === 'function') carousel.reload();

    // Focal renders the variant radios OUTSIDE the <form> element (they are linked to it by
    // the form attribute), so scope to the section rather than the form.
    var scope = mediaElement.closest('.shopify-section') || document;
    var form = document.getElementById(mediaElement.getAttribute('form-id'));

    function chosenOption() {
      var checked = scope.querySelector('.color-swatch__radio:checked');
      return checked ? (checked.getAttribute('data-option-value') || '').trim().toLowerCase() : '';
    }

    var done = false;
    function applyFilter() {
      if (done) return;
      // the tag holds the option VALUE; match it against the photo's own tag
      var wanted = chosenOption();
      var match = tagged.filter(function (item) {
        return item.getAttribute('data-orbito-option') === wanted;
      });
      if (!wanted || !match.length) return;      // e.g. swatch values are variant ids: leave it to the theme
      done = true;
      teardown();

      var heroId = match[0].getAttribute('data-media-id');
      items.forEach(function (item) {
        var option = item.getAttribute('data-orbito-option');
        var hide = option && option !== wanted;
        item.classList.toggle('is-filtered', hide);
        var isHero = item.getAttribute('data-media-id') === heroId;
        item.classList.toggle('is-selected', isHero);
        item.classList.toggle('is-initial-selected', isHero);
      });
      if (typeof carousel.reload === 'function') carousel.reload();
    }

    function onVariantChange() { applyFilter(); }
    function onSwatchClick(event) {
      if (!event.target.closest('.color-swatch, .block-swatch')) return;
      window.setTimeout(applyFilter, 0);         // let the radio settle first
    }
    function teardown() {
      if (form) form.removeEventListener('variant:change', onVariantChange);
      scope.removeEventListener('click', onSwatchClick);
    }

    if (form) form.addEventListener('variant:change', onVariantChange);
    scope.addEventListener('click', onSwatchClick);
    return { teardown: teardown };
  }, function (mediaElement, state) {
    if (state.teardown) state.teardown();
  });
})();


/* ==========================================================================
   GALLERY — slide on its own
   orbito-gallery-autoslide

   The theme's Gallery already scrolls by drag, swipe or arrows. With the arrows
   switched off nothing tells a visitor there is more to the right, so this walks
   it along one item at a time and loops back at the end.

   It gets out of the way whenever a person is involved: hover, focus, drag or
   touch pauses it, it never runs while the section is off screen, and it does
   not run at all for visitors who ask for reduced motion.
   ========================================================================== */
(function () {
  'use strict';
  if (!window.Orbito) return;

  window.Orbito.onSectionLoad('[data-orbito-autoslide]', function (gallery) {
    var wrapper = gallery.querySelector('.gallery__list-wrapper');
    if (!wrapper) return {};

    var seconds = parseFloat(gallery.getAttribute('data-orbito-autoslide')) || 4;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var paused = false;
    var onScreen = true;
    var timer = null;

    function stride() {
      var item = gallery.querySelector('.gallery__item');
      if (!item) return 0;
      var styles = window.getComputedStyle(item);
      var gap = parseFloat(styles.marginInlineStart || styles.marginLeft) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function advance() {
      if (paused || !onScreen || reduceMotion.matches) return;
      var furthest = wrapper.scrollWidth - wrapper.clientWidth;
      if (furthest <= 4) return;                       // everything already fits
      var step = stride();
      if (!step) return;
      // When less than a full item remains, finish at the end rather than wrapping
      // immediately — otherwise a row only slightly wider than the screen never moves.
      var atEnd = wrapper.scrollLeft >= furthest - 4;
      var next = atEnd ? 0 : Math.min(wrapper.scrollLeft + step, furthest);
      wrapper.scrollTo({ left: next, behavior: 'smooth' });
    }

    function hold() { paused = true; }
    function release() { paused = false; }

    ['pointerenter', 'pointerdown', 'focusin', 'touchstart'].forEach(function (type) {
      gallery.addEventListener(type, hold, { passive: true });
    });
    ['pointerleave', 'pointerup', 'focusout', 'touchend'].forEach(function (type) {
      gallery.addEventListener(type, release, { passive: true });
    });

    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
      }, { threshold: 0.2 });
      observer.observe(gallery);
    }

    timer = window.setInterval(advance, seconds * 1000);
    return { timer: timer, observer: observer, hold: hold, release: release, gallery: gallery };
  }, function (gallery, state) {
    if (state.timer) window.clearInterval(state.timer);
    if (state.observer) state.observer.disconnect();
    ['pointerenter', 'pointerdown', 'focusin', 'touchstart'].forEach(function (type) {
      gallery.removeEventListener(type, state.hold);
    });
    ['pointerleave', 'pointerup', 'focusout', 'touchend'].forEach(function (type) {
      gallery.removeEventListener(type, state.release);
    });
  });
})();
